import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import AddDesignButton from './OrderWizard/AddDesignButton';
import AddProductButton from './OrderWizard/AddProductButton';
import Modal from './OrderWizard/Modal';
import snakecase from 'lodash.snakecase';
import reCAPTCHABase from '../reCAPTCHABase';

const getStoredFormData = () => {
  try {
    const storedData = localStorage.getItem('formData');
    const parsed = JSON.parse(storedData) || {
      isStudent: true,
      school: 'Other',
    };
    return parsed;
  } catch (e) {
    return {
      isStudent: true,
      school: 'Other',
    };
  }
};

const storedStepNumber = () => {
  const saved = localStorage.getItem('modalStepNumber');
  // Default to step 0 (initial choice) for fresh starts
  return saved ? parseInt(saved, 10) : 0;
};

const openModalButton = ({ isNew, onClick }) => {
  return (
    <button
      className={`button wizard-open-button mlm ${
        isNew ? '' : 'wizard-open-button--animation'
      }`}
      type="button"
      onClick={onClick}
    >
      Start an Order
    </button>
  );
};

const OrderWizard = props => {
  const addDesignDomNode = useMemo(
    () => document.querySelector('#add-design-button'),
    []
  );
  const addProductDomNode = useMemo(
    () => document.querySelector('#add-product-button'),
    []
  );

  const desktopWizardButtonDomNode = useMemo(
    () => document.getElementById('desktop-wizard-button'),
    []
  );

  const mobileWizardButtonDomNode = useMemo(
    () => document.getElementById('mobile-wizard-button'),
    []
  );

  const quotePageButtonDomNode = useMemo(
    () => document.getElementById('quote-page-button'),
    []
  );

  const [formData, setFormDataRaw] = useState(getStoredFormData());
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalStepNumber, setModalStepNumber] = useState(storedStepNumber());
  const [previewUrl, setPreviewUrl] = useState(null);
  const [artworkDisplayUrls, setArtworkDisplayUrls] = useState([]);

  // Alias for setFormDataRaw - wrapper removed as debug code was cleaned up
  const setFormData = setFormDataRaw;

  // Use a ref to always have access to the latest formData
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  useEffect(() => {
    // Set flag and dispatch event to signal OrderWizard is ready
    window.OrderWizardReady = true;
    window.dispatchEvent(new CustomEvent('OrderWizardReady'));

    // Check for URL parameter to auto-open the modal
    let openModalTimeout = null;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openDesignStudio') === 'true') {
      // Small delay to ensure component is fully mounted
      openModalTimeout = setTimeout(() => {
        setModalIsOpen(true);
        setModalStepNumber(1);
        document.body.classList.add('wizard-no-scroll');
        // Clean up the URL parameter without reloading the page
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('openDesignStudio');
        window.history.replaceState({}, '', newUrl);
      }, 100);
    }

    // Listen for designs from the gallery
    const handleLoadDesignFromGallery = event => {
      const designData = event.detail;
      if (designData && designData.fromGallery) {
        // Merge localStorage data with current React state
        // This preserves user-typed form fields (name, email, etc.) while getting new design data
        const storedData = getStoredFormData();
        setFormData(prevFormData => ({
          ...prevFormData,
          // Only update design-related fields from localStorage
          design: storedData.design,
          product: storedData.product,
          productColor: storedData.productColor,
          designCustomization: storedData.designCustomization,
        }));

        // Only open modal if requested (first design/product)
        if (designData.openModal) {
          // If we have both product and design, jump to customization
          // Otherwise start at page 1
          setModalStepNumber(1);
          setModalIsOpen(true);
          document.body.classList.add('wizard-no-scroll');
        }
      }
    };

    // Listen for storage events to update when localStorage changes
    const handleStorageChange = e => {
      // Only handle real storage events from OTHER tabs (cross-tab sync)
      // We intentionally ignore "fake" storage events from same page because:
      // 1. They can overwrite in-progress form data that hasn't been saved yet
      // 2. The gallery integration already updates formData via LoadDesignFromGallery event
      if (
        e &&
        e.storageArea === localStorage &&
        e.key === 'formData' &&
        e.oldValue !== null
      ) {
        // Real storage event from another tab - safe to reload
        const updatedFormData = getStoredFormData();
        setFormData(updatedFormData);
      }
      // Note: We no longer handle fake storage events (e && !e.storageArea)
      // as they were causing form data to be overwritten with stale localStorage values
    };

    // Listen for event to open modal
    const handleOpenModalEvent = event => {
      // Reload formData from localStorage to ensure we have the latest state
      const latestFormData = getStoredFormData();
      setFormData(latestFormData);

      // If no modalStepNumber in localStorage, start at step 0 (InitialChoice)
      const savedStep = localStorage.getItem('modalStepNumber');
      if (!savedStep) {
        setModalStepNumber(0);
      }

      setModalIsOpen(true);
      document.body.classList.add('wizard-no-scroll');
    };

    // Save form data immediately before page unload to prevent data loss
    const handleBeforeUnload = () => {
      const currentFormData = formDataRef.current;
      if (currentFormData) {
        // Create a copy without large SVG data
        const dataToStore = { ...currentFormData };
        if (dataToStore.designCustomization) {
          const customizationCopy = { ...dataToStore.designCustomization };
          delete customizationCopy.sideSvgContent;
          delete customizationCopy.side_svg_content;
          delete customizationCopy.designPoolSvgContent;
          delete customizationCopy.design_pool_svg_content;
          dataToStore.designCustomization = customizationCopy;
        }
        try {
          localStorage.setItem('formData', JSON.stringify(dataToStore));
        } catch (e) {
          // Ignore storage errors on unload
        }
      }
    };

    window.addEventListener(
      'LoadDesignFromGallery',
      handleLoadDesignFromGallery
    );
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('OpenOrderWizardModal', handleOpenModalEvent);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Clean up timeout if component unmounts before it fires
      if (openModalTimeout) {
        clearTimeout(openModalTimeout);
      }

      window.removeEventListener(
        'LoadDesignFromGallery',
        handleLoadDesignFromGallery
      );
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('OpenOrderWizardModal', handleOpenModalEvent);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const designRequestInProgress = formData.design || formData.product;

  const clearLocalStorage = () => {
    setFormData({
      isStudent: formData.isStudent,
      school: 'Other',
      designCustomization: null,
      designNotes: null,
      orderType: null,
      uploadedArtwork: null,
      // Clear user input fields
      name: null,
      email: null,
      phoneNumber: null,
      organization: null,
      businessName: null,
      eventName: null,
      quantity: null,
      budget: null,
      deliveryDate: null,
      orderNotes: null,
    });
    setModalStepNumber(0);
    setPreviewUrl(null);
    localStorage.removeItem('modalStepNumber');
    localStorage.removeItem('formData');
    localStorage.removeItem('quoteJustSubmitted');
  };

  function handleStartOver() {
    const userConfirmed = window.confirm(
      'Are you sure you want to start over? This will clear all of your selections and inputted data.'
    );
    if (userConfirmed === true) {
      clearLocalStorage();
    }
  }

  function handleRemoveDesign() {
    if (
      window.confirm(
        'Are you sure you want to remove your design? This will remove your design from your quote request.'
      ) === true
    ) {
      handleChange('design', null);
    }
  }

  function handleRemoveProduct() {
    if (
      window.confirm(
        'Are you sure you want to remove your product? This will remove your product from your quote request.'
      ) === true
    ) {
      handleChange('product', null);
      handleChange('productColor', null);
    }
  }

  function handleChange(name, value) {
    // Special handling for designCustomization to prevent overwriting product data
    if (name === 'designCustomization') {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: value,
        // Preserve the current product data when updating designCustomization
        product: prevFormData.product,
        productColor: prevFormData.productColor,
      }));
    } else {
      // Use functional update to ensure we're always working with the latest state
      setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
    }
  }

  function updateFormDataAttributes(attributes) {
    setFormData(prevFormData => ({ ...prevFormData, ...attributes }));
  }

  function updateDesign(design) {
    handleChange('design', {
      id: design.id,
      url: design.url,
      artCode: design.artCode,
      designUrl: design.designUrl,
    });
    // Go to design studio (step 1) - skip initial choice since user chose from gallery
    setModalStepNumber(1);
    setModalIsOpen(true);
    document.body.classList.add('wizard-no-scroll');
  }

  function updateProduct(product, productColor) {
    // If we're updating the same product and no new color is provided, keep the existing color
    const shouldKeepExistingColor =
      formData.product &&
      formData.product.id === product.id &&
      !productColor &&
      formData.productColor &&
      formData.productColor.id !== 'default';

    // Ensure productColor has all required properties
    const safeProductColor = shouldKeepExistingColor
      ? formData.productColor
      : productColor || {};

    const newProductColor = {
      id: safeProductColor.id || 'default',
      name: safeProductColor.name || 'Default',
      marketingImageUrl:
        safeProductColor.marketingImageUrl ||
        safeProductColor.marketing_image_url ||
        '',
      marketing_image_url:
        safeProductColor.marketingImageUrl ||
        safeProductColor.marketing_image_url ||
        '',
      hexCode:
        safeProductColor.hexCode || safeProductColor.hex_code || '#FFFFFF',
      hex_code:
        safeProductColor.hexCode || safeProductColor.hex_code || '#FFFFFF',
    };

    const newAttributes = {
      product: {
        id: product.id,
        description: product.description,
        name: product.name,
        style: product.style,
        brandName: product.brandName,
        productUrl: product.productUrl,
      },
      productColor: newProductColor,
    };

    updateFormDataAttributes(newAttributes);

    const currentFormData = { ...formData, ...newAttributes };

    try {
      localStorage.setItem('formData', JSON.stringify(currentFormData));
    } catch (e) {}

    // Go to design studio (step 1) when adding a product
    setModalStepNumber(1);
    setModalIsOpen(true);
    document.body.classList.add('wizard-no-scroll');
  }

  const snakeCaseKeys = obj => {
    if (Array.isArray(obj)) {
      return obj.map(snakeCaseKeys);
    } else if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc, key) => {
        // Skip keys that are already snake_case (contain underscore followed by letter)
        // This prevents lodash.snakecase from converting 's3_key' to 's_3_key'
        var snakeKey = key.includes('_') ? key : snakecase(key);
        acc[snakeKey] = snakeCaseKeys(obj[key]);
        return acc;
      }, {});
    }
    return obj;
  };

  const handleSubmitQuoteRequest = () => {
    // Mark that a quote has been submitted to prevent drawer from auto-opening
    localStorage.setItem('quoteJustSubmitted', 'true');

    // Reload formData from localStorage to get the latest design customization
    // (design pool updates are saved directly to localStorage from DesignCustomizer)
    const storedFormData = getStoredFormData();

    // Use current React state for form fields since localStorage might be stale due to debounce
    // (localStorage saves are delayed by 800ms, so recently typed values may not be persisted yet)
    const currentFormData = formDataRef.current;

    // Merge: use localStorage for design data, React state for form fields
    const fullFormData = {
      ...storedFormData,
      // Override with current React state for form fields that might not be saved yet
      name: currentFormData.name || storedFormData.name,
      email: currentFormData.email || storedFormData.email,
      phoneNumber: currentFormData.phoneNumber || storedFormData.phoneNumber,
      school: currentFormData.school || storedFormData.school,
      organization: currentFormData.organization || storedFormData.organization,
      businessName: currentFormData.businessName || storedFormData.businessName,
      eventName: currentFormData.eventName || storedFormData.eventName,
      quantity: currentFormData.quantity || storedFormData.quantity,
      budget: currentFormData.budget || storedFormData.budget,
      deliveryDate: currentFormData.deliveryDate || storedFormData.deliveryDate,
      orderNotes: currentFormData.orderNotes || storedFormData.orderNotes,
      uploadedArtwork: currentFormData.uploadedArtwork || storedFormData.uploadedArtwork,
      orderType: currentFormData.orderType || storedFormData.orderType,
      isStudent: currentFormData.isStudent !== undefined ? currentFormData.isStudent : storedFormData.isStudent,
    };

    // Optimize payload: Remove large SVG content and unnecessary data
    if (fullFormData.designCustomization) {
      const cleanedCustomization = { ...fullFormData.designCustomization };
      // Remove SVG content that's too large and can be refetched
      delete cleanedCustomization.sideSvgContent;
      delete cleanedCustomization.side_svg_content;
      delete cleanedCustomization.designPoolSvgContent;
      delete cleanedCustomization.design_pool_svg_content;
      delete cleanedCustomization.pngDesigns; // Remove PNG data if present

      // Optimize design pool - only send essential data
      if (
        cleanedCustomization.designPool &&
        cleanedCustomization.designPool.length > 0
      ) {
        cleanedCustomization.designPool = cleanedCustomization.designPool.map(
          d => ({
            id: d.id,
            artCode: d.artCode,
            url: d.url || d.thumbnailUrl,
          })
        );
      }

      // Optimize sideDesigns - remove redundant data
      if (cleanedCustomization.sideDesigns) {
        const optimizedSideDesigns = {};
        Object.entries(cleanedCustomization.sideDesigns).forEach(
          ([side, designs]) => {
            if (designs && designs.length > 0) {
              optimizedSideDesigns[side] = designs.map(d => ({
                id: d.id,
                artCode: d.artCode,
                url: d.url || d.thumbnailUrl,
              }));
            }
          }
        );
        cleanedCustomization.sideDesigns = optimizedSideDesigns;
      }

      fullFormData.designCustomization = cleanedCustomization;
    }

    const quote_form = snakeCaseKeys(fullFormData);
    const payload = { quote_form: quote_form };

    // Return promise so caller can handle success/failure (e.g., reset isSubmitting state)
    return reCAPTCHABase('quote_form')
      .then(p => {
        const finalPayload = { ...payload, ...p };

        return fetch('/quote', {
          credentials: 'same-origin',
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify(finalPayload),
        });
      })
      .then(response => {
        // Parse JSON response first to get error details
        return response.json().then(data => {
          if (!response.ok) {
            // If server returned an error, throw with the error message
            if (data.message) {
              throw new Error(data.message);
            }
            throw new Error(`Server returned ${response.status}`);
          }
          return data;
        });
      })
      .then(data => {
        // Store the preview URL from the response
        if (data.preview_url) {
          setPreviewUrl(data.preview_url);
        }

        // Store artwork display URLs from the response
        if (data.artwork_urls && data.artwork_urls.length > 0) {
          setArtworkDisplayUrls(data.artwork_urls);
        }

        // Track quote submission in Google Analytics
        if (typeof window.gtag === 'function') {
          var latestData = getStoredFormData();
          window.gtag('event', 'quote_submitted', {
            event_category: 'conversion',
            event_label: latestData.orderType || 'utees_design',
          });
        }

        setModalStepNumber(3);
        // Don't clear data here - page 3 needs it to display the thank you page
        // Data will be cleared when modal is closed from page 3
      })
      .catch(error => {
        // Handle quote submission error with specific message
        const errorMessage =
          error.message ||
          'There was an error submitting your quote request. Please try again.';
        alert(errorMessage);
        console.error('Quote submission error:', error);
        // Re-throw so caller can handle (e.g., reset isSubmitting)
        throw error;
      });
  };

  // Track previous formData to detect which fields changed
  const prevFormDataRef = useRef(formData);

  useEffect(() => {
    const prevFormData = prevFormDataRef.current;
    const currentFormData = formDataRef.current;

    // List of form fields that should be saved immediately (user input fields)
    const immediateFields = [
      'name',
      'email',
      'phoneNumber',
      'school',
      'organization',
      'businessName',
      'eventName',
      'quantity',
      'budget',
      'deliveryDate',
      'orderNotes',
      'isStudent',
      'orderType',
      'uploadedArtwork',
    ];

    // Check if any immediate field changed
    const immediateFieldChanged = immediateFields.some(
      field => currentFormData[field] !== prevFormData[field]
    );

    // Update prev ref for next comparison
    prevFormDataRef.current = currentFormData;

    // Helper function to prepare data for storage
    const prepareDataForStorage = data => {
      const dataToStore = { ...data };

      // Handle designCustomization separately to avoid deep cloning everything
      if (dataToStore.designCustomization) {
        // Create a shallow copy of designCustomization
        const customizationCopy = { ...dataToStore.designCustomization };

        // Remove large SVG content fields to avoid quota issues
        delete customizationCopy.sideSvgContent;
        delete customizationCopy.side_svg_content;
        delete customizationCopy.designPoolSvgContent;
        delete customizationCopy.design_pool_svg_content;

        dataToStore.designCustomization = customizationCopy;
      }

      return dataToStore;
    };

    // Helper function to save to localStorage with error handling
    const saveToLocalStorage = dataToStore => {
      try {
        localStorage.setItem('formData', JSON.stringify(dataToStore));
      } catch (e) {
        if (e.name === 'QuotaExceededError') {
          // Try to clear old data and retry
          localStorage.removeItem('selectedDesigns');
          localStorage.removeItem('targetDesignSide');
          localStorage.removeItem('existingDesigns');

          try {
            localStorage.setItem('formData', JSON.stringify(dataToStore));
          } catch (retryError) {
            console.error('Storage quota exceeded even after cleanup');
          }
        }
      }
    };

    // If an immediate field changed, save right away to prevent data loss on refresh
    if (immediateFieldChanged) {
      const dataToStore = prepareDataForStorage(currentFormData);
      saveToLocalStorage(dataToStore);
      return; // No cleanup needed for immediate save
    }

    // For non-immediate fields (like designCustomization), use debounced save
    const timeoutId = setTimeout(() => {
      const saveToStorage = () => {
        const dataToStore = prepareDataForStorage(formDataRef.current);
        saveToLocalStorage(dataToStore);
      };

      // Use requestIdleCallback if available for better performance
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(saveToStorage, { timeout: 1000 });
      } else {
        saveToStorage();
      }
    }, 800); // Debounce for large design data

    return () => clearTimeout(timeoutId);
  }, [formData]);

  useEffect(() => {
    localStorage.setItem('modalStepNumber', modalStepNumber.toString());
  }, [modalStepNumber]);

  const handleOpenModal = useCallback(() => {
    // Only clear the quote submitted flag if we're starting fresh (no saved step or step 0/1)
    const savedStep = localStorage.getItem('modalStepNumber');
    if (!savedStep || savedStep === '0' || savedStep === '1') {
      localStorage.removeItem('quoteJustSubmitted');
    }

    // Reload formData from localStorage to ensure we have the latest state
    const latestFormData = getStoredFormData();
    setFormData(latestFormData);

    // Determine which step to start at
    // If user has already selected a design or product, skip initial choice and go to design studio
    const hasDesignOrProduct = latestFormData.design || latestFormData.product ||
      (latestFormData.designCustomization && latestFormData.designCustomization.designPool &&
       latestFormData.designCustomization.designPool.length > 0);

    if (hasDesignOrProduct) {
      // Skip initial choice, go to design studio
      setModalStepNumber(1);
    } else if (!savedStep) {
      // Fresh start with no selections - show initial choice
      setModalStepNumber(0);
    }
    // Otherwise, use whatever step was saved in localStorage

    // Track modal open in Google Analytics
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'order_wizard_open', {
        event_category: 'engagement',
      });
    }

    setModalIsOpen(true);
    document.body.classList.add('wizard-no-scroll');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle initial choice selection
  function handleInitialChoice(choice) {
    handleChange('orderType', choice);

    if (choice === 'utees_design') {
      // This is handled in InitialChoice component - closes modal and redirects
      return;
    } else if (choice === 'own_artwork' || choice === 'contact_only') {
      // Skip to quote form (step 2) for both artwork upload and contact-only
      setModalStepNumber(2);
    }
  }

  function closeModal() {
    setModalIsOpen(false);
    if (modalStepNumber >= 3) {
      // Clear everything when closing from the thank you page
      clearLocalStorage();
    }
    document.body.classList.remove('wizard-no-scroll');
  }

  // Get current product from page
  const currentProduct =
    addProductDomNode && addProductDomNode.dataset.product
      ? JSON.parse(addProductDomNode.dataset.product)
      : null;

  // Determine button text based on state
  let addProductButtonText;
  if (formData && formData.product) {
    // Check if the current page product matches the stored product (ignoring color)
    if (currentProduct && currentProduct.id === formData.product.id) {
      addProductButtonText = 'Added to Design Studio';
    } else {
      addProductButtonText = 'Replace product in Design Studio';
    }
  } else {
    addProductButtonText = 'Add to Design Studio';
  }

  const addDesignButtonText = 'Add this design to the Design Studio';

  return (
    <>
      <div
        id="wizard-bg-overlay"
        className={`wizard-bg-overlay ${
          modalIsOpen ? 'wizard-bg-overlay--active' : ''
        }`}
      ></div>
      {modalIsOpen && (
        <Modal
          isOpen={modalIsOpen}
          closeModal={closeModal}
          productGalleryUrl={props.productGalleryUrl}
          designGalleryUrl={props.designGalleryUrl}
          schools={props.schools}
          handleStartOver={handleStartOver}
          handleRemoveDesign={handleRemoveDesign}
          handleRemoveProduct={handleRemoveProduct}
          handleSubmitQuoteRequest={handleSubmitQuoteRequest}
          handleChange={handleChange}
          handleInitialChoice={handleInitialChoice}
          formData={formData}
          recaptchaKey={props.recaptchaKey}
          modalStepNumber={modalStepNumber}
          setModalStepNumber={setModalStepNumber}
          previewUrl={previewUrl}
          artworkDisplayUrls={artworkDisplayUrls}
        />
      )}
      {desktopWizardButtonDomNode &&
        createPortal(
          openModalButton({
            isNew: !designRequestInProgress,
            onClick: handleOpenModal,
          }),
          desktopWizardButtonDomNode
        )}
      {mobileWizardButtonDomNode &&
        createPortal(
          openModalButton({
            isNew: !designRequestInProgress,
            onClick: handleOpenModal,
          }),
          mobileWizardButtonDomNode
        )}
      {quotePageButtonDomNode &&
        createPortal(
          openModalButton({
            isNew: !designRequestInProgress,
            onClick: handleOpenModal,
          }),
          quotePageButtonDomNode
        )}
      {addDesignDomNode &&
        createPortal(
          <AddDesignButton
            updateDesign={updateDesign}
            buttonText={addDesignButtonText}
          />,
          addDesignDomNode
        )}
      {addProductDomNode &&
        createPortal(
          <AddProductButton
            updateProduct={updateProduct}
            buttonText={addProductButtonText}
            isProductAdded={addProductButtonText === 'Product Added'}
          />,
          addProductDomNode
        )}
    </>
  );
};

export default OrderWizard;
